import AnimatedSection from "@/components/AnimatedSection";

interface PageHeroProps {
  badge?: string;
  headline: string;
  subtext: string;
  primaryCTA?: string;
  secondaryCTA?: string;
  backgroundImage?: string;
  center?: boolean;
}

export default function PageHero({
  badge,
  headline,
  subtext,
  primaryCTA,
  secondaryCTA,
  backgroundImage,
  center = false,
}: PageHeroProps) {
  return (
    <section className="relative min-h-[480px] md:min-h-[520px] lg:min-h-[560px] flex items-center overflow-hidden bg-hero-gradient">
      {backgroundImage && (
        <>
          <div className="absolute inset-0">
            <img
              src={backgroundImage}
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-primary-950/85" />
          </div>
        </>
      )}

      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="container-astro relative z-10 pt-24 md:pt-28 lg:pt-32 pb-16 md:pb-20">
        <div className={center ? "max-w-3xl mx-auto text-center" : "max-w-2xl lg:max-w-3xl"}>
          {badge && (
            <AnimatedSection delay={0}>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
                <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">
                  {badge}
                </span>
              </div>
            </AnimatedSection>
          )}

          <AnimatedSection delay={100}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-background-50 leading-[1.1] mb-6 font-heading tracking-tight">
              {headline}
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <p className="text-base md:text-lg text-background-400 leading-relaxed mb-8 max-w-2xl">
              {subtext}
            </p>
          </AnimatedSection>

          <AnimatedSection delay={300}>
            <div className={`flex flex-col sm:flex-row items-${center ? "center justify-center" : "start sm:items-center"} gap-4`}>
              {primaryCTA && (
                <a
                  href="/contact-us"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
                >
                  {primaryCTA}
                  <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              )}
              {secondaryCTA && (
                <a
                  href="/hire-laravel-developer"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white text-sm font-semibold rounded-xl hover:bg-white/10 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover group"
                >
                  {secondaryCTA}
                  <i className="ri-chat-1-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
    </section>
  );
}